import { ComponentIds, HierarchyUtils } from '@inter-ikea-core/core';
import { AnimateHingeDoorsComponent, ToggleDoorsComponent } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { getSelectedEntity } from 'ducks/core';
import { isDoorsVisible } from 'ducks/layout';
import { getSelectedGroupId } from 'ducks/products';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const useAnimateDoors = () => {
  const core = useCore();
  const selectedGroupId = useSelector(getSelectedGroupId);
  const selectedEntity = useSelector(getSelectedEntity);
  const [prevGroup, setPrevGroup] = useState<string>(selectedGroupId);
  const active = useSelector(isDoorsVisible);

  const animate = useCallback(() => {
    const doors = core.entities.getByComponent(ComponentIds.DOOR_COMPONENT_ID);
    let angle: number;
    let toggleDoors: boolean;

    if (selectedGroupId === '') {
      angle = active ? -45 : -83;
      toggleDoors = !active;
    } else if (active) {
      angle = 0;
      toggleDoors = false;
    } else {
      angle = -83;
      toggleDoors = true;
    }

    doors.forEach(d => {
      if (selectedEntity && selectedEntity.entityId === d.id) {
        d.addComponent(new AnimateHingeDoorsComponent(0, false));
      } else {
        d.addComponent(new AnimateHingeDoorsComponent(angle, toggleDoors));
      }
    });
  }, [active, core, selectedGroupId, selectedEntity]);

  /**
   * This effect is needed since "active" and "selectedGroupId" can
   * be out of sync. This will push re-render until they are in sync
   */
  useEffect(() => {
    setPrevGroup(selectedGroupId);
  }, [selectedGroupId]);

  /**
   * Animate doors when a model is loaded, e.g changing frame
   * size or replacing door
   */
  useEffect(() => {
    const animateOnLoad = () => {
      animate();
    };

    return core.subscribe('modelLoaded', animateOnLoad);
  }, [animate, core]);

  /**
   * Animate doors when selected product group changes
   */
  useEffect(() => {
    animate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevGroup, active, selectedEntity]);
};

const useToggleDoors = () => {
  const core = useCore();
  const active = useSelector(isDoorsVisible);

  const doors = core.entities.getByComponent(ComponentIds.DOOR_FRONT_COMPONENT_ID);
  const doorDescendants = doors.flatMap(door => HierarchyUtils.descendants(door));
  [...doors, ...doorDescendants].forEach(entity => {
    entity.addComponent(new ToggleDoorsComponent(!active));
  });
  if (active) {
    core.entities.setAllVisible(doors, true, true);
  } else {
    core.entities.setAllVisible(doors, false, true);
  }
};

export const AnimateDoors = () => {
  useAnimateDoors();
  return <></>;
};

export const ToggleDoors = () => {
  useToggleDoors();
  return <></>;
};
